/**
 * CardDisplay — Renders a single playing card
 *
 * Supports face-up/face-down, suit colors (red/black), three sizes,
 * reveal animation, and winning-hand highlighting.
 * ARIA labels for accessibility.
 *
 * Requirements: 17.3, 17.4, 17.9, 24.1, 25.2
 */

import React from 'react';
import { CardDisplayProps, Suit } from '../types';
import './CardDisplay.css';

const SUIT_SYMBOLS: Record<Suit, string> = {
  hearts: '♥',
  diamonds: '♦',
  clubs: '♣',
  spades: '♠',
};

const SUIT_NAMES: Record<Suit, string> = {
  hearts: 'Hearts',
  diamonds: 'Diamonds',
  clubs: 'Clubs',
  spades: 'Spades',
};

const RANK_NAMES: Record<string, string> = {
  '2': 'Two',
  '3': 'Three',
  '4': 'Four',
  '5': 'Five',
  '6': 'Six',
  '7': 'Seven',
  '8': 'Eight',
  '9': 'Nine',
  '10': 'Ten',
  J: 'Jack',
  Q: 'Queen',
  K: 'King',
  A: 'Ace',
};

function isRedSuit(suit: Suit): boolean {
  return suit === 'hearts' || suit === 'diamonds';
}

export const CardDisplay: React.FC<CardDisplayProps> = ({
  card,
  size = 'medium',
  faceDown = false,
  animateReveal = false,
  highlighted = false,
}) => {
  const isFaceDown = faceDown || card === null;

  const classNames = [
    'poker-card',
    `poker-card--${size}`,
    isFaceDown ? 'poker-card--face-down' : '',
    !isFaceDown && card ? (isRedSuit(card.suit) ? 'poker-card--red' : 'poker-card--black') : '',
    highlighted ? 'poker-card--highlighted' : '',
    animateReveal && !isFaceDown ? 'poker-card--animate-reveal' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const ariaLabel = isFaceDown
    ? 'Face-down card'
    : card
      ? `${RANK_NAMES[card.rank] || card.rank} of ${SUIT_NAMES[card.suit]}`
      : 'Unknown card';

  return (
    <div className={classNames} role="img" aria-label={ariaLabel}>
      {isFaceDown ? (
        <div className="poker-card__back-pattern" aria-hidden="true">
          ♠
        </div>
      ) : card ? (
        <>
          <span className="poker-card__rank">{card.rank}</span>
          <span className="poker-card__suit" aria-hidden="true">
            {SUIT_SYMBOLS[card.suit]}
          </span>
        </>
      ) : null}
    </div>
  );
};

export default CardDisplay;
